import { useState, useEffect, useRef } from 'react'
import { useS4LContext } from '@s4l/react-context'
import type { StayForLong, UseCaseGetterNames } from '@s4l/stayforlong'

interface Options {
  lazy?: boolean
}

const defaultOptions: Options = { lazy: false }

const isOptions = (obj: unknown): obj is Options => {
  return typeof obj === 'object' && obj !== null && 'lazy' in obj
}

type UseCaseState<TData, TError> =
  | { fulfilled: false; loading: boolean; error: undefined; data: undefined }
  | { fulfilled: true; loading: false; error: TError; data: undefined }
  | { fulfilled: true; loading: false; error: undefined; data: TData }

type result<T extends UseCaseGetterNames> = UseCaseState<
  Awaited<ReturnType<StayForLong[T]['execute']>>[1],
  Awaited<ReturnType<StayForLong[T]['execute']>>[0]
> & {
  execute: (params?: Parameters<StayForLong[T]['execute']>[0]) => void
}

// Overload signatures
export function useUseCase<T extends UseCaseGetterNames>(useCase: T): result<T>

export function useUseCase<T extends UseCaseGetterNames>(
  useCase: T,
  input: Parameters<StayForLong[T]['execute']>[0]
): result<T>

export function useUseCase<T extends UseCaseGetterNames>(useCase: T, options: Options): result<T>

export function useUseCase<T extends UseCaseGetterNames>(
  useCase: T,
  input: Parameters<StayForLong[T]['execute']>[0],
  options: Options
): result<T>

export function useUseCase<T extends UseCaseGetterNames>(
  useCase: T,
  param1?: Parameters<StayForLong[T]['execute']>[0] | Options,
  param2?: Options
) {
  let inputParams: Parameters<StayForLong[T]['execute']>[0]
  let options: Options = defaultOptions

  // case: 3 params (useCase, input, options)
  if (param2) {
    inputParams = param1 as Parameters<StayForLong[T]['execute']>[0]
    options = param2
  } else if (param1) {
    if (isOptions(param1)) {
      // case: 2 params (useCase, options) - no input
      inputParams = undefined
      options = param1
    } else {
      // case: 2 params (useCase, input)
      inputParams = param1 as Parameters<StayForLong[T]['execute']>[0]
      options = defaultOptions
    }
  }

  const { lazy = defaultOptions.lazy } = options ?? {}

  type Result = Awaited<ReturnType<StayForLong[T]['execute']>>
  type ErrorType = Result[0]
  type DataType = Result[1]

  const { domain } = useS4LContext()

  const [state, setState] = useState<UseCaseState<DataType, ErrorType>>({
    fulfilled: false,
    loading: false,
    error: undefined,
    data: undefined,
  })

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)
  const abortControllerRef = useRef<AbortController | null>(null)

  const execute = async (params?: Parameters<StayForLong[T]['execute']>[0]) => {
    // Cancel any pending execution
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this execution
    abortControllerRef.current = new AbortController()
    const currentController = abortControllerRef.current

    if (!isMountedRef.current) return

    setState({
      fulfilled: false,
      loading: true,
      error: undefined,
      data: undefined,
    })

    try {
      // @ts-expect-error: typescript limitation
      const [useCaseError, result] = await domain[useCase].execute(params ?? inputParams)

      // Check if this execution was aborted or component unmounted
      if (currentController.signal.aborted || !isMountedRef.current) {
        return
      }

      if (useCaseError) {
        setState({
          fulfilled: true,
          loading: false,
          error: useCaseError,
          data: undefined,
        })
      } else {
        setState({
          fulfilled: true,
          loading: false,
          error: undefined,
          data: result,
        })
      }
    } catch (error) {
      // Check if this execution was aborted or component unmounted
      if (currentController.signal.aborted || !isMountedRef.current) {
        return
      }

      if (error instanceof Error) {
        setState({
          fulfilled: true,
          loading: false,
          error,
          data: undefined,
        })
      }
    }
  }

  useEffect(() => {
    if (!lazy) {
      execute(inputParams)
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false
      // Abort any pending executions
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!state.fulfilled) {
    return {
      fulfilled: false,
      loading: state.loading,
      error: undefined,
      data: undefined,
      execute,
    } as const
  }

  if (state.error) {
    return {
      fulfilled: true,
      loading: false,
      error: state.error,
      data: undefined,
      execute,
    } as const
  }

  if (state.data) {
    return {
      fulfilled: true,
      loading: false,
      error: undefined,
      data: state.data,
      execute,
    } as const
  }

  return {
    fulfilled: false,
    loading: false,
    error: undefined,
    data: undefined,
    execute,
  } as const
}
